'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var ch4o = require('ch4o');
var validator = require('@shadom/validator');
var translator = require('@shadom/translator');

const DCEXP_I_START = '§';
const DCEXP_I_END = '§';
class ClusterExpression extends String {
    raw;
    constructor(exp) {
        super(`${DCEXP_I_START}${exp}${DCEXP_I_END}`);
        this.raw = exp;
    }
    toString(raw = false) {
        if (raw) {
            return this.raw;
        }
        return super.toString();
    }
}

/**
 *
 */
const RE_SEP_LS = /\s*,\s*/;
const RE_SEP_PATH = /\s*\.\s*/;
class DataTrackTask extends ch4o.Task {
    id = '';
    checker;
    constructor(prop, validator) {
        super(prop);
        this.id = prop;
        this.checker = validator;
    }
    exec(node) {
        if (node === null) {
            return null;
        }
        const { id } = this;
        const sub = node[id];
        if (!node.hasOwnProperty(id)) {
            return null;
        }
        return this.checker.exec(sub) ? sub : null;
    }
}
////////////////
// TODO transpile static value at compile time
function defineDefaultValue(prop, data) {
    return Object.defineProperty(this, prop, {
        get() {
            return ch4o.exec(data);
        }
    });
}
function parseFnList(fnListExp = null, lib) {
    if (!fnListExp) {
        return new ch4o.TaskChain([]);
    }
    const fnNames = fnListExp.split(RE_SEP_LS);
    // debugger;
    return new ch4o.TaskChain(fnNames.map(fnName => {
        const fn = lib[fnName];
        if (fn) {
            return fn;
        }
        throw new ReferenceError(`Task Fn "${fnName}" not found!`);
    }));
}
class Cluster {
    // [x: string]: any;
    path;
    validator;
    defaultValue;
    translator;
    constructor(dcc) {
        const validatorChain = parseFnList(dcc.validator, validator.lib);
        // Append a normal validator to ensure that the value of data node always exists
        validatorChain.add(validator.isRealValue);
        // Path 
        this.path = new ch4o.TaskChain(dcc.path.split(RE_SEP_PATH)
            .map(nodeName => new DataTrackTask(nodeName, validatorChain)));
        this.translator = parseFnList(dcc.translator, translator.lib);
        this.validator = validatorChain;
        // DefaultValue should be string expression, so it can be newly generated every time we get value from Cluster
        let defaultValue = dcc.defaultValue;
        if (!validator.isStr(defaultValue)) {
            defaultValue = JSON.stringify(defaultValue);
        }
        defineDefaultValue.call(this, 'defaultValue', defaultValue);
    }
    get config() {
        const dcc = { path: '' };
        if (validator.isRealValue(this.defaultValue)) {
            dcc.defaultValue = this.defaultValue;
        }
        dcc.path = this.path.names.join('.');
        return ['validator', 'translator'].reduce((dco, key) => {
            const ls = this[key].names.join();
            if (ls.length > 0) {
                dco[key] = ls;
            }
            return dco;
        }, dcc);
    }
    async parse(data) {
        let value = await this.path.exec(data);
        if (!await this.validate(value)) {
            return this.defaultValue;
        }
        return await this.translate(value);
    }
    async validate(data) {
        return await this.validator.exec(data);
    }
    async translate(data) {
        return await this.translator.exec(data);
    }
}

/**
 * data cluster compiler
 * @author chengxiao
 *
 * 字符串表达式:
 *   >a.b.c; validator, validator, ...; defaultValue; translator, translator, ...; inherits<
 * 缩写:
 *     >a.b.c<                                1
 *     >a.b.c=defaultValue<                   5
 *     >a.b.c!validator<                      3
 *     >a.b.c!validator=defaultValue<         7
 *     >!validator<                           2
 *     >!validator=defaultValue<              6
 *     >=defaultValue<                        4
 *     >~translator<                          8
 *     >>inherits<                            16
 */
const [DATA_PATH, VALIDATOR, DEFAULT_VALUE, TRANSLATOR, INHERITS] = [
    ch4o.ID('path'),
    ch4o.ID('validator'),
    ch4o.ID('defaultValue'),
    ch4o.ID('translator'),
    ch4o.ID('inherits'),
];
const ABBR_TPL_TABLE = {
    1: [ch4o.TPL `${DATA_PATH}`, /^(?<path>[^=!~]+)$/],
    5: [ch4o.TPL `${DATA_PATH}=${DEFAULT_VALUE}`, /^(?<path>[^=!~]+)=(?<defaultValue>[^\n]+)$/],
    3: [ch4o.TPL `${DATA_PATH}!${VALIDATOR}`, /^(?<path>[^=!~]+)!(?<validator>[^!]+)$/],
    7: [ch4o.TPL `${DATA_PATH}!${VALIDATOR}=${DEFAULT_VALUE}`, /^(?<path>[^=!~]+)!(?<validator>[^=!~]+)=(?<defaultValue>[^\n]+)$/],
    2: [ch4o.TPL `!${VALIDATOR}`, /^!(?<validator>[^=!~]+)$/],
    6: [ch4o.TPL `!${VALIDATOR}=${DEFAULT_VALUE}`, /^!(?<validator>[^=!~]+)=(?<defaultValue>[^\n]+)$/],
    4: [ch4o.TPL `=${DEFAULT_VALUE}`, /^=(?<defaultValue>[^\n]+)$/],
    8: [ch4o.TPL `~${TRANSLATOR}`, /^~(?<translator>[^=!~]+)$/],
    16: [ch4o.TPL `>${INHERITS}`, /^>(?<inherits>[a-zA-Z0-9]+)/],
};
// 字符串表达式写全的情况下，顺序固定, 长度（段SEG数量）固定 = 5
const INDICATERS = [
    [0b00001, String(DATA_PATH), ''],
    [0b00010, String(VALIDATOR), '!'],
    [0b00100, String(DEFAULT_VALUE), '='],
    [0b01000, String(TRANSLATOR), '~'],
    [0b10000, String(INHERITS), '>']
];
// 字符串Cluster表达式 dcexp
const RE_DCEXP = /^§(?<exp>[^§]+)§$/i;
// segment seperator
const RE_SEG_SEP = /\s*;\s*/;
// list seprator
// const RE_STR_BLOCK = /((['"])(?!\1*)\1)/g;
/**
 * 把DCEXP编译成配置对象
 * @param dcexp data cluster string expression
 * @return
 */
function parse(dcexp) {
    if (!(dcexp instanceof ClusterExpression)) {
        if (typeof dcexp === 'string') {
            dcexp = new ClusterExpression(dcexp);
        }
        else {
            return new Cluster(dcexp);
        }
    }
    const match = RE_DCEXP.exec(dcexp.toString());
    const dccProto = {
        path: '',
    };
    dcexp = match.groups.exp;
    // 对常规格式的处理（分号分隔）
    let segments = dcexp.split(RE_SEG_SEP);
    if (segments.length === INDICATERS.length) {
        const dcc = segments.reduce((dcc, val, index) => {
            let [id, key, value] = INDICATERS[index];
            dcc[key] = val;
            return dcc;
        }, dccProto);
        // parseDefaultValue(dcc);
        // dco.defaultValue = JSON.parse(dco.defaultValue);
        return new Cluster(dcc);
    }
    // 解析简写表达式
    const dcc = Object.entries(ABBR_TPL_TABLE).reduce((dcc, [id, [tpl, re]]) => {
        const match = re.exec(dcexp.toString());
        match && Object.assign(dcc, match.groups);
        return dcc;
    }, dccProto);
    // parseDefaultValue(dcc);
    // console.log('compi', dcc)
    return new Cluster(dcc);
}
/**
 * 上
 * @param dco DC config object
 * @return
 */
function stringify(dco) {
    if ((dco instanceof Cluster)) {
        dco = dco.config;
    }
    dco = { ...dco };
    if (dco.hasOwnProperty('defaultValue')) {
        dco.defaultValue = JSON.stringify(dco.defaultValue);
    }
    let sum = 0;
    let dca = INDICATERS.map(([id, name]) => {
        if (dco.hasOwnProperty(name)) {
            sum += id;
            return dco[name];
        }
        return null;
    });
    // Simplify str expression to abbr
    if (sum in ABBR_TPL_TABLE) {
        let abbrTPL = ABBR_TPL_TABLE[sum];
        return new ClusterExpression(`${abbrTPL[0](dco)}`);
        /*
         *        return dca.reduce((abbr, segValue, index) => {
         *            if (segValue) {
         *                let [segID, segName, segPrefix] = INDICATERS[index];
         *
         *                return abbr.replaceAll(segName, segValue);
         *            }
         *            return abbr;
         *        }, `>${abbrTPL}<`);
         */
    }
    // console.log(sum);
    let str = dca.join(';');
    return new ClusterExpression(str);
    // return `${DCEXP_I_START}${str}${DCEXP_I_END}`;
}

class DataCluster {
    cluster = {};
    keys = [];
    constructor(config) {
        if (!config) {
            config = {};
        }
        // TODO inherists
        // resolveRelation(config);
        Object.entries(config).reduce((container, [clusterName, config]) => {
            const cluster = parse(config);
            container[clusterName] = cluster;
            this.keys.push(clusterName);
            return container;
        }, this.cluster);
    }
    async parse(data) {
        if (this.keys.length === 0) {
            return data;
        }
        const values = await Promise.all(this.keys.map((key) => {
            return this.cluster[key].parse(data);
        }));
        return values.reduce((values, value, index) => {
            values[this.keys[index]] = value;
            return values;
        }, {});
    }
    /**
     * select data in different places
     * @param selector
     * @returns
     */
    select(selector) {
    }
    /**
     * get cluster of data or all data
     * @param key cluster name
     * @return
     */
    checkout(key) {
    }
}

function isDCE(expr) {
    return expr.startsWith(DCEXP_I_START) && expr.endsWith(DCEXP_I_END);
}

exports.Cluster = Cluster;
exports.ClusterExpression = ClusterExpression;
exports.DCEXP_I_END = DCEXP_I_END;
exports.DCEXP_I_START = DCEXP_I_START;
exports.DataCluster = DataCluster;
exports.isDCE = isDCE;
exports.parse = parse;
exports.stringify = stringify;
